import { expandAppEntitlementView } from 'lib/actions/apps/app-entitlement'
import { AppResourceRollupItemView, AppResourceTypeRollupItemView } from 'lib/actions/apps/app-resource-search'
import { AppResourceValue, expandAppResourceView } from 'lib/actions/apps/app-resources'
import { expandValue } from 'lib/rpcts'
import { App } from 'pbts/c1/api/app/v1/app'
import {
    AppResourceGrant,
    AppResourceRef,
    AppResourceType,
    AppResourceView,
    HighRiskRolesAndCountStruct,
    HighRiskRolesAndGrantsStruct,
    GetAppResourceGrantsSearchTermsResponse, AppResourceTypeView,
} from 'pbts/c1/api/app/v1/app_resource'
import { AppResourceSearchClient, AppResourceServiceClient } from 'pbts/c1/api/app/v1/app_resource.client'
import { AppResourceOwnerRef, AppResourceOwnerRefreshRef } from 'pbts/c1/api/app/v1/app_resource_owner'
import { AppResourceOwnersClient, AppResourceOwnersSearchClient } from 'pbts/c1/api/app/v1/app_resource_owner.client'
import { FacetValue } from 'pbts/c1/api/search/v1/facet'
import { User } from 'pbts/c1/api/user/v1/user'
import { createPagedListApi } from 'redux_toolkit/lib/createPagedListApi'
import { transformUserValueArrayResponse } from 'redux_toolkit/users'
import { createMutationApi } from './lib/createMutationApi'
import { createUnaryApi } from './lib/createUnaryApi'

const transformAppResourceValueArrayResponse = (resp): Array<AppResourceValue> => {
    return resp.list.map((v): AppResourceValue => expandAppResourceView(v, resp.expanded))
}

export interface AppResourceRollupItemViewWithId extends AppResourceRollupItemView {
    id: string
}

export interface AppResourceTypeRollupItemViewWithId extends AppResourceTypeRollupItemView {
    id: string
}

export interface AppResourceValueAndInactiveAppOwner {
    appResource: AppResourceValue | undefined
    appResourceOwners: User[]
}

const transformAppResourceRollupResponse = (resp): Array<AppResourceRollupItemViewWithId> => {
    return resp.list.map((msg): AppResourceRollupItemView => {
        return {
            ...AppResourceView.create(msg?.item),
            appEntitlements: msg?.entitlements,
            app: expandValue<App>(msg.item?.appPath || '', App, resp.expanded),
            appResourceType: expandValue<AppResourceType>(
                msg?.item?.resourceTypePath || '',
                AppResourceType,
                resp.expanded,
            ),
        }
    })
}

export const appResourceOwnersHook = createPagedListApi(AppResourceOwnersClient, 'list', undefined, AppResourceOwnerRef.typeName)

export const debouncedAppResourceOwnersHook = createUnaryApi(AppResourceOwnersClient, 'list', undefined, AppResourceOwnerRefreshRef.typeName)

export const appResourceSearchApi = createPagedListApi(
    AppResourceSearchClient,
    'searchAppResources',
    transformAppResourceValueArrayResponse,
    AppResourceRef.typeName,
)

export const appResourceTypeSearchApi = createPagedListApi(AppResourceSearchClient, 'searchAppResourceTypes')

export const appResourceSearchRollupApi = createPagedListApi(
    AppResourceSearchClient,
    'searchAppResourcesRollup',
    transformAppResourceRollupResponse,
)

const transformAppResourceTypeRollupResponse = (resp): Array<AppResourceTypeRollupItemViewWithId> => {
    return resp.list.map((msg): AppResourceTypeRollupItemView => {
        return {
            ...AppResourceTypeView.create(msg?.item),
            app: expandValue<App>(msg.item?.appPath || '', App, msg.expanded),
            resourceCount: msg?.resourceCount,
        }
    })
}

export const searchAppResourceTypesRollup = createPagedListApi(
    AppResourceSearchClient,
    'searchAppResourceTypesRollup',
    transformAppResourceTypeRollupResponse,
    undefined,
)

const UnknownIdentityUserId = 'unknown_app_user_id'

export interface AppResourceGrantValue extends AppResourceGrant {
    id: string
}

const transformAppResourceGrantValueRow = (resp): Array<AppResourceGrantValue> =>
    resp.list.map((v): AppResourceGrantValue => ({ id: v.appUser?.id || UnknownIdentityUserId, ...v }))

export const searchAppResourceGrantsApi = createPagedListApi(
    AppResourceSearchClient,
    'searchAppResourceGrants',
    transformAppResourceGrantValueRow,
)

const transformFacetResponse = (d: GetAppResourceGrantsSearchTermsResponse): Array<FacetValue> => {
    const item = d?.facets?.facets?.[0]?.item
    return item?.oneofKind === 'value' ? item.value?.values : []
}

export const getSearchAppResourceGrantsSearchTerms = createUnaryApi(
    AppResourceSearchClient,
    'getAppResourceGrantsSearchTerms',
    transformFacetResponse,
)

export const searchAppResourceGrants = createPagedListApi(
    AppResourceSearchClient,
    'searchAppResourceGrants',
    transformAppResourceGrantValueRow,
)

export const highRiskRolesandGrants = createPagedListApi(
    AppResourceSearchClient,
    'highRiskRolesAndGrants',
    (resp): Array<HighRiskRolesAndCountStruct> =>
        resp.list.map((v): HighRiskRolesAndCountStruct => (v)),
    HighRiskRolesAndCountStruct.typeName,
)

export const searchAppResourcesWithDeactivatedOwner = createPagedListApi(
    AppResourceSearchClient,
    'searchAppResourcesWithDeactivatedOwner',
    (resp): AppResourceValueAndInactiveAppOwner[] => {
        return resp.list.map((v) : AppResourceValueAndInactiveAppOwner => {
            const appResource: AppResourceValue = expandAppResourceView(v.appResourceView, resp.expanded)
            return { appResource, appResourceOwners: v.appResourceOwners }
        })
    },
    AppResourceRef.typeName,
)

export const highRiskRolesandGrantsCount = createUnaryApi(AppResourceSearchClient, 'highRiskRolesAndGrantsCount')
export const highRiskRolesandGrantsEntitlements = createPagedListApi(
    AppResourceSearchClient,
    'highRiskRolesAndGrantsEntitlements',
    (resp): Array<HighRiskRolesAndGrantsStruct> =>
        resp.list.map((v): HighRiskRolesAndGrantsStruct => (v)),
    HighRiskRolesAndGrantsStruct.typeName,
)

export const createManuallyManagedAppResource = createMutationApi(
    AppResourceServiceClient,
    'createManuallyManagedResource',
    (ae) => expandAppEntitlementView(ae.appEntitlementView, ae.expanded),
)
export const deleteManuallyManagedAppEntitlement = createMutationApi(
    AppResourceServiceClient,
    'deleteManuallyManagedEntitlement',
    (ae) => expandAppEntitlementView(ae.appEntitlementView, ae.expanded),
)
export const getAppResourceApi = createUnaryApi(
    AppResourceServiceClient,
    'get',
    (resp): AppResourceValue => expandAppResourceView(resp.appResourceView, resp.expanded),
    AppResourceRef.typeName,
)
export const updateAppResourceApi = createMutationApi(
    AppResourceServiceClient,
    'update',
    (resp): AppResourceValue => expandAppResourceView(resp.appResourceView, resp.expanded),
    AppResourceRef.typeName,
)

export const searchAppResourceOwnersApi = createPagedListApi(
    AppResourceOwnersSearchClient,
    'search',
    transformUserValueArrayResponse,
    AppResourceOwnerRef.typeName,
)

export const addAppResourceOwnersApi = createMutationApi(
    AppResourceOwnersClient,
    'add',
    undefined,
    AppResourceOwnerRef.typeName,
)

export const removeAppResourceOwnersApi = createMutationApi(
    AppResourceOwnersClient,
    'remove',
    undefined,
    AppResourceOwnerRef.typeName,
)
